import * as React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { AudienceModalHeader } from '@/features/editor/widgets/shared/modals/audience/modal-content';
import { Footer } from '@/features/editor/widgets/shared/modals/commons';
import { GradientModal } from '@/components/gradient-modal';
import { centered } from '@/components/use-shared-element';
import { useAudienceQueryBuilder } from '@/features/editor/widgets/shared/modals/use-audience-query-builder';
import { EditorContext } from '@/features/editor/context/editor-context';
import { QueryBuilder } from '@/components/query-builder';
import { QBItemSelection } from '@/components/query-builder/models';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { Role } from '@/utils/definitions';

export interface PriceTestAudienceModalProps {
  fromRef: React.MutableRefObject<HTMLElement>;
  isVisible: boolean;
  setIsVisible: (state: boolean) => void;
}

export function PriceTestAudienceModal({
  fromRef,
  isVisible,
  setIsVisible,
}: PriceTestAudienceModalProps) {
  const {
    account: {
      store: { currency: defaultCurrency, role },
    },
  } = useContext(AccountContext);
  const {
    resources,
    experienceState: { currentExperience, setTargetingAudience },
  } = useContext(EditorContext);

  const toRect = centered(60, 90);

  const { audienceProps, currency } = useAudienceQueryBuilder(
    resources,
    currentExperience?.targeting?.other,
    false,
  );

  const initialState: QBItemSelection[] = [
    {
      qbProps: { ...currency, disableOrCond: true, disableRemove: true },
      values: [{ op: `is`, value: defaultCurrency || `USD` }],
    },
  ];

  useEffect(() => {
    setTargetingAudience(
      [],
      currentExperience?.targeting?.other || initialState,
    );
  }, []);

  const isSystemUser = useMemo(() => role === Role.SYSTEM, [role]);

  const andOrTargetingEnabled = useFeatureBit(
    FeatureBit.AND_OR_QUERY_BUILDER_TARGETING,
  );

  const predefinedAudienceEnabled = useFeatureBit(
    FeatureBit.PRICE_TEST_PREDEFINED_AUDIENCE,
  );

  const filteredProps = useMemo(
    () =>
      audienceProps
        ?.filter((prop) =>
          [
            `Cart Currency`,
            `Country`,
            `State`,
            `City`,
            `UTM Source`,
            `UTM Medium`,
            `UTM Campaign`,
            `UTM Content`,
            `UTM Term`,
            `Customer Tags`,
            ...(predefinedAudienceEnabled ? [`Predefined Audience`] : []),
          ].includes(prop.caption),
        )
        ?.map((props) => {
          if (props.caption === `Cart Currency`) {
            return {
              ...props,
              disableRemove: true,
              disableOrCond: !isSystemUser,
            };
          }
          if (props.caption === `Predefined Audience`) {
            return {
              ...props,
              options: props.options?.filter(
                (o) => o.value === `Never Purchased`,
              ),
            };
          }
          return props;
        }),
    [audienceProps],
  );

  const onSave = () => {
    setIsVisible(false);
  };

  const onClose = () => {
    setIsVisible(false);
  };

  const onQueryBuilderChange = (selection: QBItemSelection[]) => {
    setTargetingAudience([], selection);
  };

  return (
    <GradientModal
      closeTop="0.5rem"
      closeRight="2.5rem"
      fromStyle={{
        borderRadius: `5rem`,
        backgroundColor: `#DEDEDE`,
        padding: `2rem 0 0 0`,
      }}
      toStyle={{
        borderRadius: `1rem`,
        backgroundColor: `#FFFFFF`,
        padding: `2rem 0 0 0`,
      }}
      isVisible={isVisible}
      showBackdrop
      targetPosAndSize={toRect}
      fromRef={fromRef}
      overflowTop={{ heightInRem: 8 }}
      overflowBottom={{ heightInRem: 8, turnPointInPercent: 30 }}
      onClose={onClose}
      header={<AudienceModalHeader />}
      footer={<Footer onClick={onSave} text="Select" />}
    >
      <Container>
        <QueryBuilder
          initialProps={filteredProps}
          initialState={currentExperience?.targeting?.other || initialState}
          onChange={onQueryBuilderChange}
          andOrEnabled={andOrTargetingEnabled}
        />
      </Container>
    </GradientModal>
  );
}

const Container = styled.div`
  margin: 2rem 1.5rem;
  text-transform: none;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 2rem;

  text-align: center;
  color: #9ba7b3;
  letter-spacing: -0.38px;
  font-family: Inter, serif;
  font-weight: 500;
  font-size: 1.4rem;
`;
